import type { PremiumFeatures } from '@lub0v/santa-utils';
import { BoxTheme, BoxMode, type BoxCreateInput, type CashCurrency } from '@lub0v/santa-utils';

export const NEW_BOX_STEPS = [
  'name',
  'organizer',
  'picture',
  'exchange',
  'cost',
  'letter',
  'email',
  'participants',
  'review',
] as const;

export type NewBoxStep = (typeof NEW_BOX_STEPS)[number];

export type ExchangeMethod = 'meet' | 'send' | 'pickup' | 'later';
export type BudgetPreset = 'none' | 'low' | 'mid' | 'custom';

export interface NewBoxDraft {
  theme: BoxTheme;
  key: string;
  name: string;
  exchange: ExchangeMethod | null;
  isPremium: boolean;
  features?: PremiumFeatures;
  useNames: boolean;
  usePost: boolean;
  isPhoneRequired: boolean;
  useCashLimit: boolean;
  cashLimit: number;
  cashLimitMin: number;
  cashLimitCurrency: CashCurrency | '';
  budgetPreset: BudgetPreset | null;
  useWish: boolean | null;
  picture: string;
  logo: string | null;
  participates: boolean | null;
  organizerName: string;
  email: string;
  participantNames: string[];
}

export function emptyDraft(name: string, currency: CashCurrency): NewBoxDraft {
  return {
    theme: BoxTheme.HALLOWEEN,
    key: '',
    name,
    exchange: null,
    isPremium: false,
    useNames: true,
    usePost: false,
    isPhoneRequired: false,
    useCashLimit: false,
    cashLimit: 0,
    cashLimitMin: 0,
    cashLimitCurrency: currency,
    budgetPreset: null,
    useWish: null,
    picture: 'santa',
    logo: null,
    participates: null,
    organizerName: '',
    email: '',
    participantNames: [],
  };
}

export function toCreateInput(
  draft: NewBoxDraft,
  extra: Partial<BoxCreateInput> = {},
): BoxCreateInput {
  return {
    theme: draft.theme,
    key: draft.key,
    name: draft.name.trim(),
    picture: draft.picture,
    logo: draft.logo || undefined,
    usePost: draft.exchange === 'send',
    usePickupPoint: draft.exchange === 'pickup',
    isPhoneRequired: draft.exchange === 'send' || draft.exchange === 'pickup',
    useCashLimit: Boolean(draft.budgetPreset && draft.budgetPreset !== 'none'),
    cashLimit: draft.cashLimit,
    cashLimitMin: draft.cashLimitMin,
    cashLimitCurrency: draft.cashLimitCurrency || undefined,
    useWish: draft.useWish ?? true,
    useNames: draft.useNames,
    mode: BoxMode.FULL,
    ...extra,
  } as BoxCreateInput;
}

export function nextStep(step: NewBoxStep, askEmail: boolean): NewBoxStep {
  if (step === 'letter' && !askEmail) {
    return 'participants';
  }
  const index = NEW_BOX_STEPS.indexOf(step);
  return NEW_BOX_STEPS[Math.min(NEW_BOX_STEPS.length - 1, index + 1)];
}

export function prevStep(step: NewBoxStep, askEmail: boolean): NewBoxStep {
  if (step === 'participants' && !askEmail) {
    return 'letter';
  }
  const index = NEW_BOX_STEPS.indexOf(step);
  return NEW_BOX_STEPS[Math.max(0, index - 1)];
}

/** Steps the wizard actually shows — email is skipped when we already have one. */
export function visibleNewBoxSteps(askEmail: boolean): NewBoxStep[] {
  return askEmail ? [...NEW_BOX_STEPS] : NEW_BOX_STEPS.filter((step) => step !== 'email');
}

export function newBoxStepCopyKey(step: NewBoxStep): string {
  return step === 'participants' ? 'people' : step;
}
